import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';

import sharp from 'sharp';
import type * as zod from 'zod';

import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { FTPServer } from '@sb/integrations/implementations/FTPServer/implementation/FTPServer';
import type { S3Bucket } from '@sb/integrations/implementations/S3Bucket/implementation/S3Bucket';
import type { DeviceCommand } from '@sb/integrations/types';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

const log = makeNamespacedLog('captureStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class CaptureStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestSaveFilepath: null,
      captureTimestamp: null,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info('capture.play', 'Playing Capture step');

      // Use type assertion since useCppCameraBot may not be in the feature flag types yet
      const useCppCameraBot = getFeatureFlagSync('useCppCameraBot', false);

      let image: ArrayBuffer;

      if (useCppCameraBot) {
        if (!this.args.cameraEquipmentId) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No camera equipment ID provided',
          });
        }

        const cameraEquipment = this.routineContext.equipment.getDeviceById(
          this.args.cameraEquipmentId,
        ) as Camera | undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
          });
        }

        image = await cameraEquipment.getColorFrame();
      } else {
        // Use old WristCamera implementation
        const wristCamera = this.routineContext.equipment.getWristCamera();

        if (!wristCamera) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No wrist camera found in Equipment',
          });
        }

        if (!this.args.camera) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'Camera configuration is required',
          });
        }

        image = await wristCamera.getColorFrame(this.args.camera);
      }

      // If an ROI was provided, crop the image before saving
      let imageToSave: ArrayBuffer = image;

      if (this.args.regionOfInterest) {
        const preprocessed = await this.routineContext.vision.preprocessImage(
          image,
          this.args.regionOfInterest,
        );

        imageToSave = preprocessed.croppedImage;

        log.info('capture.play', 'Applied region of interest to image');
      }

      log.info('capture.play', 'Image captured, converting to PNG');

      // Validate image data before processing
      if (!imageToSave || imageToSave.byteLength === 0) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: 'Received empty image data from camera',
        });
      }

      // Check minimum size for any valid image format (most formats need at least a few bytes for headers)
      if (imageToSave.byteLength < 2) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: 'Received incomplete image data (too small)',
        });
      }

      // Generate a unique temporary file name
      const tempFilename = `capture_${Date.now()}_${Math.random().toString(36).substring(7)}.png`;
      const tempFilePath = path.join(os.tmpdir(), tempFilename);

      // Convert image to PNG using sharp and save to temporary file
      // Sharp supports many input formats: JPEG, PNG, WebP, TIFF, GIF, SVG, etc.
      try {
        await sharp(imageToSave).png().toFile(tempFilePath);
      } catch (error) {
        log.error('capture.play', 'Error converting image with sharp', error);

        // Check if it's an image format/decoding error
        if (
          error instanceof Error &&
          (error.message.includes(
            'Input buffer contains unsupported image format',
          ) ||
            error.message.includes('Premature end') ||
            error.message.includes('Vips') ||
            error.message.includes('unsupported') ||
            error.message.includes('corrupt'))
        ) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Image data is corrupted, incomplete, or in an unsupported format: ${error.message}. This may be due to network issues or camera encoding problems.`,
          });
        }

        throw error;
      }

      log.info('capture.play', 'Converted and saved PNG image to temp file', {
        tempFilePath,
      });

      // Initialize capture timestamp (available for expressions)
      const now = new Date();
      const pad = (n: number, w = 2) => n.toString().padStart(w, '0');

      const formattedTimestamp = `${now.getFullYear()}-${pad(
        now.getMonth() + 1,
      )}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(
        now.getMinutes(),
      )}-${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;

      this.setVariable('captureTimestamp', formattedTimestamp);

      // Evaluate the filepath expression to get the dynamic filename
      const remotePath = await this.routineContext.evaluateExpression(
        this.args.saveFilepath,
      );

      // Ensure the evaluated filepath is a string
      if (typeof remotePath !== 'string') {
        throw new Error(
          `Filepath expression must evaluate to a string, received ${typeof remotePath}`,
        );
      }

      if (!remotePath) {
        throw new Error(
          'Filepath expression must evaluate to a non-empty string',
        );
      }

      if (!remotePath.endsWith('.png')) {
        throw new Error(
          `Filepath must end with .png (Filepath: ${remotePath})`,
        );
      }

      // Get the storage equipment (FTP Server or S3 Bucket)
      const storageEquipment = this.routineContext.equipment.getDeviceById(
        this.args.saveEquipmentId,
      );

      if (!storageEquipment) {
        throw new Error(
          `Storage equipment not found: ${this.args.saveEquipmentId}`,
        );
      }

      // Determine equipment type and create appropriate command
      const equipmentKind = storageEquipment.kind;
      let uploadCommand: DeviceCommand;

      if (equipmentKind === 'FTPServer') {
        uploadCommand = {
          kind: 'FTPServerCommand',
          command: {
            command: 'uploadFile',
            localPath: tempFilePath,
            remotePath,
          },
        };
      } else if (equipmentKind === 'S3Bucket') {
        uploadCommand = {
          kind: 'S3BucketCommand',
          command: {
            command: 'uploadFile',
            localPath: tempFilePath,
            remotePath,
          },
        };
      } else {
        throw new Error(`Unsupported storage equipment type: ${equipmentKind}`);
      }

      log.info('capture.play', 'Uploading image to storage', {
        remotePath,
        equipmentId: this.args.saveEquipmentId,
        equipmentKind,
      });

      try {
        if (equipmentKind === 'FTPServer') {
          await (storageEquipment as FTPServer).executeCommand(uploadCommand);
        } else if (equipmentKind === 'S3Bucket') {
          await (storageEquipment as S3Bucket).executeCommand(uploadCommand);
        }

        this.setVariable('latestSaveFilepath', remotePath);
      } catch (uploadError) {
        const errorMessage =
          uploadError instanceof Error
            ? uploadError.message
            : 'Unknown upload error';

        log.error('capture.play', 'Upload failed', {
          error: uploadError,
          remotePath,
          equipmentId: this.args.saveEquipmentId,
          equipmentKind,
        });

        throw new Error(`Failed to upload image to storage: ${errorMessage}`);
      } finally {
        // Clean up temporary file even on error
        await fs.promises.unlink(tempFilePath).catch((unlinkError) => {
          log.warn('capture.play', 'Failed to delete temporary file', {
            tempFilePath,
            error: unlinkError,
          });
        });
      }

      log.info('capture.play', 'Capture step completed');
    } catch (error) {
      log.error('capture.play', 'Capture failed', error);

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Capture',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
